import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, euro, formatDate } from "@/lib/api";
import { Baby, Coins, FileWarning, CalendarCheck, Bell } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const MESE_LABEL = { "01": "Gen", "02": "Feb", "03": "Mar", "04": "Apr", "05": "Mag", "06": "Giu", "07": "Lug", "08": "Ago", "09": "Set", "10": "Ott", "11": "Nov", "12": "Dic" };

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/dashboard").then((r) => { setData(r.data); setError(""); }).catch((err) => setError(err?.response?.status === 401 ? "Sessione scaduta: esci e accedi nuovamente." : "Dashboard non raggiungibile. Riavvia APiccoliPassi."));
  }, []);

  if (error) return <div className="text-red-500 py-20 text-center font-semibold">{error}</div>;
  if (!data) return <div className="text-[#a89a8a] py-20 text-center" data-testid="dashboard-loading">Caricamento...</div>;

  const sectionColors = ["#F4A261", "#81B29A", "#E07A5F", "#6D8AA8", "#9B7EBD"];
  const kpis = [
    ...Object.entries(data.bimbi_per_sezione || {}).map(([section, count], index) => ({ label: section, value: count, icon: Baby, color: sectionColors[index % sectionColors.length], testid: `kpi-sezione-${index}` })),
    { label: "Incassi del mese", value: euro(data.incassi_mese), icon: Coins, color: "#E07A5F", testid: "kpi-incassi" },
    { label: "Fatture in attesa", value: data.fatture_attesa, icon: FileWarning, color: "#F2CC8F", testid: "kpi-fatture" },
    { label: "Presenti oggi", value: `${data.presenze_oggi}/${data.totale_iscritti}`, icon: CalendarCheck, color: "#81B29A", testid: "kpi-presenze" },
  ];

  const chartData = data.incassi_per_mese.map((m) => ({
    mese: MESE_LABEL[m.mese.slice(5)] || m.mese, totale: m.totale,
  }));

  return (
    <div className="space-y-8 animate-fade-up" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3D405B] tracking-tight">Cruscotto</h1>
          <p className="text-[#6C757D] mt-1 text-base">Panoramica di oggi, {formatDate(data.oggi)}</p>
        </div>
        {data.assenze_nuove > 0 && (
          <Link to="/presenze" data-testid="assenze-alert-link"
            className="flex items-center gap-2 rounded-full bg-amber-100 border border-amber-300 px-4 py-2 text-sm font-semibold text-amber-800 hover:bg-amber-200 transition">
            <Bell className="w-4 h-4" /> {data.assenze_nuove} comunicazioni assenza da leggere
          </Link>
        )}
      </div>

      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
        {kpis.map((k, i) => (
          <div key={k.label} className="card-warm p-5 animate-fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={k.testid}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: k.color + "22" }}>
              <k.icon className="w-5 h-5" style={{ color: k.color }} />
            </div>
            <p className="text-2xl font-heading font-semibold text-[#3D405B]">{k.value}</p>
            <p className="text-xs font-medium text-[#6C757D] mt-1">{k.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="card-warm p-6 lg:col-span-3">
          <h2 className="font-heading text-lg font-semibold text-[#3D405B] mb-4">Incassi ultimi 6 mesi</h2>
          <div className="h-64" data-testid="incassi-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0e4d7" />
                <XAxis dataKey="mese" tick={{ fontSize: 12, fill: "#6C757D" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#6C757D" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => euro(v)} cursor={{ fill: "#FAF6F0" }} />
                <Bar dataKey="totale" fill="#E07A5F" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card-warm p-6 lg:col-span-2">
          <h2 className="font-heading text-lg font-semibold text-[#3D405B] mb-4">Ultimi movimenti incassi</h2>
          <div className="space-y-3" data-testid="ultimi-incassi-list">
            {data.ultimi_incassi.length === 0 && <p className="text-sm text-[#a89a8a]">Nessun incasso registrato.</p>}
            {data.ultimi_incassi.map((i) => (
              <div key={i.id} className="flex items-center justify-between rounded-xl bg-[#FAF6F0] px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-[#3D405B]">{i.descrizione}</p>
                  <p className="text-xs text-[#a89a8a]">{formatDate(i.data)} · {i.metodo}</p>
                </div>
                <span className={`text-sm font-bold ${i.stato === "pagato" ? "text-emerald-600" : i.stato === "scaduto" ? "text-red-500" : "text-amber-600"}`}>
                  {euro(i.importo)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
